import { Component, Input, OnChanges, SimpleChanges, AfterViewInit, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { LoggerService } from '../../../core/services/logger.service';

interface ProrrogaStats {
  prorroga: string;
  cantidad: number;
}

interface LegendItem {
  label: string;
  value: number;
  percent: string;
  color: string;
}

interface ChartData {
  labels: string[];
  series: number[];
  colors: string[];
}

@Component({
  selector: 'app-control-dus-prorroga-chart',
  standalone: true,
  imports: [MatCardModule, CommonModule],
  templateUrl: './control-dus-prorroga-chart.component.html',
  styleUrls: ['./control-dus-prorroga-chart.component.scss'],
})
export class ControlDusProrrogaChartComponent implements AfterViewInit, OnChanges {
  @Input() statistics: ProrrogaStats[] = [];

  // Exposed to template
  total: number = 0;
  legendItems: LegendItem[] = [];

  private isBrowser: boolean;
  private chart: any = null;
  private chartData: ChartData = {
    labels: [],
    series: [],
    colors: [],
  };

  // Colores para prórroga
  private readonly prorrogaColors: { [key: string]: string } = {
    SI: '#ff9800',
    NO: '#2ed47e',
    'SIN DATOS': '#6c757d',
  };

  constructor(
    @Inject(PLATFORM_ID) platformId: object,
    private logger: LoggerService
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  async ngAfterViewInit(): Promise<void> {
    if (this.statistics.length > 0) {
      this.processData();
      await this.renderChart();
    }
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['statistics'] && this.statistics.length > 0) {
      this.processData();
      if (this.chart) {
        await this.updateChart();
      } else {
        await this.renderChart();
      }
    }
  }

  /**
   * Procesa los datos de estadísticas del backend para el gráfico
   */
  private processData(): void {
    const labels: string[] = [];
    const series: number[] = [];
    const colors: string[] = [];

    // Orden preferido: NO primero, luego SI
    const ordenPreferido = ['NO', 'SI', 'SIN DATOS'];
    const statsMap = new Map(this.statistics.map(s => [s.prorroga, s.cantidad]));

    ordenPreferido.forEach((key) => {
      if (statsMap.has(key)) {
        const label = key === 'SI' ? 'Con Prórroga' : key === 'NO' ? 'Sin Prórroga' : 'Sin Datos';
        labels.push(label);
        series.push(statsMap.get(key) || 0);
        colors.push(this.prorrogaColors[key]);
      }
    });

    if (labels.length === 0) {
      this.chartData = {
        labels: ['Sin Datos'],
        series: [0],
        colors: ['#6c757d'],
      };
    } else {
      this.chartData = { labels, series, colors };
    }

    // Calculate total and legend items for template
    this.total = series.reduce((a, b) => a + b, 0);
    this.legendItems = labels.map((label, i) => ({
      label,
      value: series[i],
      percent: this.total > 0 ? ((series[i] / this.total) * 100).toFixed(1) : '0',
      color: colors[i],
    }));
  }

  /**
   * Renderiza el gráfico usando ApexCharts (Donut)
   */
  async renderChart(): Promise<void> {
    if (!this.isBrowser) return;

    try {
      const ApexCharts = (await import('apexcharts')).default;

      const options = {
        series: this.chartData.series,
        chart: {
          height: 180,
          type: 'donut',
        },
        labels: this.chartData.labels,
        colors: this.chartData.colors,
        plotOptions: {
          pie: {
            donut: {
              size: '75%',
              labels: { show: false }
            },
          },
        },
        legend: { show: false }, // We use custom legend
        dataLabels: { enabled: false }, // Clean donut without labels
        stroke: { width: 2, colors: ['#fff'] },
        tooltip: {
          y: {
            formatter: (val: number) => {
              const percent = this.total > 0 ? ((val / this.total) * 100).toFixed(1) : '0';
              return `${val} DUS (${percent}%)`;
            },
          },
        },
      } as any;

      const container = document.querySelector('#control_dus_prorroga_pie_chart');
      if (!container) {
        this.logger.warn('Contenedor del gráfico de prórroga no encontrado', 'ControlDusProrrogaChartComponent');
        return;
      }

      this.chart = new ApexCharts(container, options);
      this.chart.render();
    } catch (error) {
      this.logger.error('Error al renderizar el gráfico de prórroga', error as Error, 'ControlDusProrrogaChartComponent');
    }
  }

  /**
   * Actualiza el gráfico con nuevos datos
   */
  async updateChart(): Promise<void> {
    if (!this.isBrowser) return;

    if (this.chart) {
      try {
        this.chart.updateOptions({
          series: this.chartData.series,
          labels: this.chartData.labels,
          colors: this.chartData.colors,
        });
      } catch (error) {
        this.logger.error('Error al actualizar el gráfico de prórroga', error as Error, 'ControlDusProrrogaChartComponent');
      }
    } else {
      await this.renderChart();
    }
  }
}
